/**
 * Installs the module-scoped auth-reset callback used by the SDK's
 * `onUnauthenticated` hook (and Phase 4's inactivity logout). Captures
 * `useNavigate`, `useLocation`, `useAuthSession().resetSession`, and
 * `notifications.show` from the running React tree, then registers a
 * stable callback that forwards into `performAuthReset`.
 *
 * Mounted as a sibling of `<Outlet />` inside `<AuthenticatedLayout>`.
 * Renders nothing.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { notifications } from "@mantine/notifications";
import { useAuthSession } from "@/app/auth/AuthSessionContext";
import {
  performAuthReset,
  setAuthResetCallback,
} from "@/app/auth/performAuthReset";
import type { AuthResetReason } from "@/app/auth/notifications";

export const AuthResetGate = (): null => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetSession } = useAuthSession();

  // Latest values stay in refs so the installed callback is stable
  // across renders (re-installing on every render would race with the
  // SDK firing during the in-between window). Refs are updated inside
  // an effect to comply with React Hooks v7's `react-hooks/refs` rule
  // (refs may not be mutated during render). The effect runs after the
  // commit, which is fine: the SDK's 401 callback runs during a network
  // response, well after the surrounding commit has settled.
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const resetSessionRef = useRef(resetSession);
  useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
    resetSessionRef.current = resetSession;
  }, [navigate, location, resetSession]);

  const callback = useCallback((reason: AuthResetReason) => {
    performAuthReset(reason, {
      navigate: (to) => {
        const result = navigateRef.current(to);
        if (result && typeof (result as Promise<void>).catch === "function") {
          (result as Promise<void>).catch(console.error);
        }
      },
      location: locationRef.current,
      resetSession: () => resetSessionRef.current(),
      notify: (n) => notifications.show(n),
    });
  }, []);

  useEffect(() => {
    return setAuthResetCallback(callback);
  }, [callback]);

  return null;
};
